import type {
  AudienceKind,
  ConsumerContractArtifact,
  ConsumerContractRegistryManifest,
  ConsumerContractSubscription,
  RenderSourceKind,
  SourceCategory,
  SubscriptionKind,
  UsageKind,
} from './consumerContractRegistry.types';
import {
  isValidAccessMode,
  isValidAdditionalPersistedCopy,
  isValidOperationEffect,
  isValidRiskClassification,
  isValidWriteBack,
} from './consumerContractRegistry.operationEffects';
import {
  isValidChangePropagation,
  isValidDomainOwner,
  isValidRelationshipKind,
  isValidSafeguard,
  PUBLIC_PDF_MAPPER,
} from './consumerContractRegistry.relationships';
import { validateSeedReferences } from './consumerContractRegistry.seedDefinitions';

const SUBSCRIPTION_KINDS: SubscriptionKind[] = ['direct', 'indirect', 'aggregate'];
const SOURCE_CATEGORIES: SourceCategory[] = ['canonical_payload', 'quote_owned', 'derived_cache'];
const USAGE_KINDS: UsageKind[] = ['displayed', 'exported', 'calculated', 'conditional', 'omitted'];
const RENDER_SOURCES: RenderSourceKind[] = ['live_render', 'stored_png', 'mixed'];
const AUDIENCES: AudienceKind[] = ['staff', 'customer', 'supplier', 'internal'];

export interface ManifestValidationIssue {
  path: string;
  message: string;
}

export interface ManifestValidationResult {
  valid: boolean;
  issues: ManifestValidationIssue[];
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function validateSubscription(
  sub: ConsumerContractSubscription,
  artifactId: string,
  seenIds: Set<string>,
  issues: ManifestValidationIssue[],
): void {
  const base = `artifacts.${artifactId}.subscriptions.${sub.id}`;

  if (!isNonEmptyString(sub.id)) {
    issues.push({ path: base, message: 'Subscription id is required' });
  } else if (seenIds.has(sub.id)) {
    issues.push({ path: base, message: `Duplicate subscription id "${sub.id}"` });
  } else {
    seenIds.add(sub.id);
  }

  for (const key of [
    'fieldKey',
    'subscribedField',
    'sourcePath',
    'outputLabelOrSlot',
    'transformationRule',
    'fallbackBehaviour',
    'codeReference',
  ] as const) {
    if (!isNonEmptyString(sub[key])) {
      issues.push({ path: `${base}.${key}`, message: `${key} is required` });
    }
  }

  if (!SOURCE_CATEGORIES.includes(sub.sourceCategory)) {
    issues.push({ path: `${base}.sourceCategory`, message: `Invalid sourceCategory "${sub.sourceCategory}"` });
  }
  if (!USAGE_KINDS.includes(sub.usage)) {
    issues.push({ path: `${base}.usage`, message: `Invalid usage "${sub.usage}"` });
  }
  if (!SUBSCRIPTION_KINDS.includes(sub.subscriptionKind)) {
    issues.push({ path: `${base}.subscriptionKind`, message: `Invalid subscriptionKind "${sub.subscriptionKind}"` });
  }
  if (sub.subscriptionKind === 'indirect' && !sub.viaBuilder?.trim()) {
    issues.push({ path: `${base}.viaBuilder`, message: 'viaBuilder is required for indirect subscriptions' });
  }
  if (sub.subscriptionKind === 'aggregate' && !sub.excludeFromFieldIndex) {
    issues.push({ path: `${base}.excludeFromFieldIndex`, message: 'Aggregate subscriptions must set excludeFromFieldIndex' });
  }

  if (!isValidAccessMode(sub.accessMode)) {
    issues.push({ path: `${base}.accessMode`, message: `Invalid accessMode "${sub.accessMode}"` });
  }
  if (!isValidOperationEffect(sub.operation)) {
    issues.push({ path: `${base}.operation`, message: `Invalid operation "${sub.operation}"` });
  }
  if (!isValidWriteBack(sub.writeBack)) {
    issues.push({ path: `${base}.writeBack`, message: `Invalid writeBack "${sub.writeBack}"` });
  }
  if (!isValidAdditionalPersistedCopy(sub.additionalPersistedCopy)) {
    issues.push({ path: `${base}.additionalPersistedCopy`, message: `Invalid additionalPersistedCopy "${sub.additionalPersistedCopy}"` });
  }
  if (!isValidRiskClassification(sub.riskClassification)) {
    issues.push({ path: `${base}.riskClassification`, message: `Invalid riskClassification "${sub.riskClassification}"` });
  }
  if (!isNonEmptyString(sub.riskRationale)) {
    issues.push({ path: `${base}.riskRationale`, message: 'riskRationale is required' });
  }
  if (!isValidDomainOwner(sub.domainOwner)) {
    issues.push({ path: `${base}.domainOwner`, message: `Invalid domainOwner "${sub.domainOwner}"` });
  }
  if (!isNonEmptyString(sub.currentReadSource)) {
    issues.push({ path: `${base}.currentReadSource`, message: 'currentReadSource is required' });
  }
  if (!isValidChangePropagation(sub.changePropagation)) {
    issues.push({ path: `${base}.changePropagation`, message: `Invalid changePropagation "${sub.changePropagation}"` });
  }
  if (!isNonEmptyString(sub.propagationRationale)) {
    issues.push({ path: `${base}.propagationRationale`, message: 'propagationRationale is required' });
  }
  if (!isValidSafeguard(sub.safeguard)) {
    issues.push({ path: `${base}.safeguard`, message: `Invalid safeguard "${sub.safeguard}"` });
  }
  if (!isValidRelationshipKind(sub.relationshipKind)) {
    issues.push({ path: `${base}.relationshipKind`, message: `Invalid relationshipKind "${sub.relationshipKind}"` });
  }
  if (
    sub.relationshipKind === 'canonical_reference' &&
    sub.riskClassification !== 'expected_neutral'
  ) {
    issues.push({
      path: `${base}.riskClassification`,
      message: 'Canonical payload reference must have expected_neutral operation risk',
    });
  }
  if (
    sub.viaBuilder === PUBLIC_PDF_MAPPER &&
    sub.riskClassification !== 'expected_neutral'
  ) {
    issues.push({
      path: `${base}.riskClassification`,
      message: 'Public PDF mapper field subscriptions must have expected_neutral operation risk',
    });
  }
  if (
    sub.viaBuilder === PUBLIC_PDF_MAPPER &&
    sub.changePropagation !== 'generated_from_shared_contract'
  ) {
    issues.push({
      path: `${base}.changePropagation`,
      message: 'Public PDF mapper field subscriptions must have generated_from_shared_contract propagation (intentional Edge Function boundary)',
    });
  }
  if (
    sub.subscriptionKind === 'indirect' &&
    (sub.riskClassification === 'drift_risk' || sub.riskClassification === 'conflicting_authority')
  ) {
    issues.push({
      path: `${base}.riskClassification`,
      message: 'Indirect shared-builder subscriptions must not be classified as drift_risk or conflicting_authority',
    });
  }
}

function validateArtifact(
  artifact: ConsumerContractArtifact,
  seenArtifactIds: Set<string>,
  seenSubIds: Set<string>,
  issues: ManifestValidationIssue[],
): void {
  const base = `artifacts.${artifact.id}`;

  if (!isNonEmptyString(artifact.id)) {
    issues.push({ path: base, message: 'Artifact id is required' });
  } else if (seenArtifactIds.has(artifact.id)) {
    issues.push({ path: base, message: `Duplicate artifact id "${artifact.id}"` });
  } else {
    seenArtifactIds.add(artifact.id);
  }

  for (const key of ['name', 'description', 'entryPoint'] as const) {
    if (!isNonEmptyString(artifact[key])) {
      issues.push({ path: `${base}.${key}`, message: `${key} is required` });
    }
  }

  if (!AUDIENCES.includes(artifact.audience)) {
    issues.push({ path: `${base}.audience`, message: `Invalid audience "${artifact.audience}"` });
  }
  if (!RENDER_SOURCES.includes(artifact.renderSource)) {
    issues.push({ path: `${base}.renderSource`, message: `Invalid renderSource "${artifact.renderSource}"` });
  }

  if (!Array.isArray(artifact.subscriptions) || artifact.subscriptions.length === 0) {
    issues.push({ path: `${base}.subscriptions`, message: 'At least one subscription is required' });
  } else {
    for (const sub of artifact.subscriptions) {
      validateSubscription(sub, artifact.id, seenSubIds, issues);
    }
  }
}

export function validateConsumerContractManifest(
  manifest: ConsumerContractRegistryManifest,
): ManifestValidationResult {
  const issues: ManifestValidationIssue[] = [];

  if (manifest.schemaVersion !== '1.0') {
    issues.push({ path: 'schemaVersion', message: `Expected schemaVersion "1.0", got "${manifest.schemaVersion}"` });
  }
  if (!isNonEmptyString(manifest.manifestVersion)) {
    issues.push({ path: 'manifestVersion', message: 'manifestVersion is required' });
  }
  if (!isNonEmptyString(manifest.lastReviewedAt)) {
    issues.push({ path: 'lastReviewedAt', message: 'lastReviewedAt is required' });
  }
  if (!isNonEmptyString(manifest.disclaimer)) {
    issues.push({ path: 'disclaimer', message: 'disclaimer is required' });
  }

  if (!Array.isArray(manifest.artifacts) || manifest.artifacts.length === 0) {
    issues.push({ path: 'artifacts', message: 'At least one artifact is required' });
  } else {
    const seenArtifactIds = new Set<string>();
    const seenSubIds = new Set<string>();
    for (const artifact of manifest.artifacts) {
      validateArtifact(artifact, seenArtifactIds, seenSubIds, issues);
    }
    const allSubs = manifest.artifacts.flatMap((a) => a.subscriptions);
    for (const seedIssue of validateSeedReferences(allSubs)) {
      issues.push(seedIssue);
    }
  }

  return { valid: issues.length === 0, issues };
}
